import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { filter } from 'rxjs';
import { RoleAccess } from 'src/app/rgs/components/company-data/role/models/role-access.model';
import { UserComplete } from 'src/app/rgs/components/company-data/user/models/user-complete.model';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { AppMenuitemComponent } from '../menuItem/app.menuitem.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  imports: [AppMenuitemComponent],
})
export class AppMenuComponent implements OnInit {
  private readonly keycloak = inject(Keycloak);
  private readonly permissionsService = inject(PermissionsService);

  public model: any[] = [];

  public ngOnInit() {
    this.permissionsService.user$.pipe(filter((user) => !!user)).subscribe((user) => {
      if (this.userRoles.includes('admin')) {
        this.model = this.adminMenus;
      } else {
        this.model = this.getUserMenus(user);
      }
    });
  }

  public get userRoles() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    return roles;
  }

  public get adminMenus() {
    return [
      {
        label: 'sidebar.admin',
        icon: 'fa fa-database fa-fw',
        routerLink: ['/admin'],
        items: [
          {
            label: 'sidebar.users',
            icon: 'fa fa-users fa-fw',
            routerLink: ['/admin/users'],
          },
          {
            label: 'sidebar.roles',
            icon: 'fa fa-unlock fa-fw',
            routerLink: ['/admin/roles'],
          },
          {
            label: 'sidebar.modules',
            icon: 'fa fa-cubes fa-fw',
            routerLink: ['/admin/modules'],
          },
          {
            label: 'sidebar.licenses',
            icon: 'fa fa-id-card fa-fw',
            routerLink: ['/admin/licenses'],
          },
          {
            label: 'sidebar.companies',
            icon: 'fa fa-industry fa-fw',
            routerLink: ['/admin/companies'],
          },
          {
            label: 'sidebar.wiki',
            icon: 'fa fa-book fa-fw',
            routerLink: ['/admin/wiki'],
          },
          {
            label: 'sidebar.strategy',
            icon: 'fa fa-chess-knight fa-fw',
            routerLink: ['/admin/strategy'],
          },
          {
            label: 'sidebar.reportTasks',
            icon: 'fa fa-tasks fa-fw',
            routerLink: ['/admin/report-tasks'],
          },
          {
            label: 'Label',
            icon: 'fa fa-dw fa-tags fa-fw',
            items: [
              {
                label: 'sidebar.objectCharacteristics',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/admin/objectCategory'],
              },
              {
                label: 'sidebar.layerFeatures',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/admin/layerFeatures'],
              },
              {
                label: 'sidebar.templates',
                icon: 'fa fa-dw fa-file fa-fw',
                routerLink: ['/admin/documentcode'],
              },
            ],
          },
          {
            label: 'sidebar.elements',
            icon: 'fa fa-box fa-fw',
            items: [
              {
                label: 'sidebar.groups',
                icon: 'fa fa-list fa-fw',
                routerLink: ['/admin/elementgroup'],
              },
              {
                label: 'sidebar.elements',
                icon: 'fa fa-box fa-fw',
                routerLink: ['/admin/element'],
              },
            ],
          },
          {
            label: 'sidebar.inspection',
            icon: 'fa fa-eye fa-list fa-fw',
            items: [
              {
                label: 'sidebar.inspectionList',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/admin/inspection-list-list'],
              },
              {
                label: 'sidebar.method',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/admin/inspection-method-list'],
              },
            ],
          },
          {
            label: 'Scenario',
            icon: 'fas fa-code-branch fa-fw',
            items: [
              {
                label: 'sidebar.pricebook',
                icon: 'fa fa-wallet fa-fw',
                routerLink: ['/admin/pricebook'],
              },
            ],
          },
        ],
      },
    ];
  }

  public getUserMenus(user: UserComplete | undefined) {
    const menus: unknown[] = [];

    if (ArrayUtils.ensureArray(user?.license).length > 0) {
      const menu: any = {
        label: 'sidebar.projectData',
        icon: 'fa fa-tasks fa-fw',
        items: [],
      };

      if (this.permissionsService.hasModulePermission('objects', RoleAccess.READ, user)) {
        menu.items.push({
          label: 'sidebar.objects',
          icon: 'fa fa-buildings fa-fw',
          routerLink: ['/objects'],
        });
      }

      if (this.permissionsService.hasModulePermission('inspections', RoleAccess.READ, user)) {
        menu.items.push({
          label: 'collections.inspections.plural',
          icon: 'fa-solid fa-eye fa-fw',
          routerLink: ['/inspections'],
        });
      }

      if (this.permissionsService.hasModulePermission('scenarios', RoleAccess.READ, user)) {
        menu.items.push({
          label: 'collections.scenarios.plural',
          icon: 'fa-solid fa-code-branch fa-fw',
          routerLink: ['/scenarios'],
        });
      }

      if (this.permissionsService.hasModulePermission('dataAnalysis', RoleAccess.READ, user)) {
        menu.items.push({
          label: 'sidebar.dataAnalysis',
          icon: 'fa-solid fa-chart-pie fa-fw',
          routerLink: ['/data-analysis'],
        });
      }

      if (this.permissionsService.hasModulePermission('tasks', RoleAccess.READ, user)) {
        menu.items.push({
          label: 'sidebar.tasks',
          icon: 'fa-solid fa-list-check fa-fw',
          routerLink: ['/tasks'],
        });
      }

      if (menu.items.length > 0) {
        menus.push(menu);
      }
    }

    const companyItems: unknown[] = [];

    if (this.permissionsService.hasModulePermission('companyProfile', RoleAccess.READ, user)) {
      companyItems.push({
        label: 'sidebar.companyProfile',
        icon: 'fa fa-id-card fa-fw',
        routerLink: ['/companyProfile'],
      });
    }

    if (this.permissionsService.hasModulePermission('clients', RoleAccess.READ, user)) {
      companyItems.push({
        label: 'sidebar.clients',
        icon: 'fa fa-users fa-fw',
        routerLink: ['/clients'],
      });
    }

    if (this.permissionsService.hasModulePermission('users', RoleAccess.READ, user)) {
      companyItems.push({
        label: 'sidebar.user',
        icon: 'fa fa-user fa-fw',
        routerLink: ['/users'],
      });
    }

    if (this.permissionsService.hasModulePermission('roles', RoleAccess.READ, user)) {
      companyItems.push({
        label: 'sidebar.roles',
        icon: 'fa fa-unlock fa-fw',
        routerLink: ['/roles'],
      });
    }

    const companyMenus: unknown[] = [];

    if (companyItems.length > 0) {
      companyMenus.push(
        ...[
          { separator: true },
          {
            label: 'sidebar.companyData',
            icon: 'fa fa-building fa-fw',
            items: companyItems,
          },
        ],
      );
    }

    const masterdataMenus: unknown[] = [
      { separator: true },
      {
        label: 'sidebar.masterData',
        icon: 'fa fa-database fa-fw',
        routerLink: ['/master'],
        items: [
          {
            label: 'sidebar.strategy',
            icon: 'fa fa-chess-knight fa-fw',
            routerLink: ['/master/strategy'],
          },
          {
            label: 'Label',
            icon: 'fa fa-dw fa-tags fa-fw',
            items: [
              {
                label: 'sidebar.objectCharacteristics',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/master/objectCategory'],
              },
              {
                label: 'sidebar.layerFeatures',
                icon: 'fa fa-dw fa-tags fa-fw',
                routerLink: ['/master/layerFeatures'],
              },
              {
                label: 'sidebar.templates',
                icon: 'fa fa-dw fa-file fa-fw',
                routerLink: ['/master/documentcode'],
              },
            ],
          },
          {
            label: 'sidebar.elements',
            icon: 'fa fa-cube fa-fw',
            items: [
              {
                label: 'sidebar.groups',
                icon: 'fa fa-cubes fa-fw',
                routerLink: ['/master/elementgroup'],
              },
              {
                label: 'sidebar.elements',
                icon: 'fa fa-cube fa-fw',
                routerLink: ['/master/element'],
              },
            ],
          },
          {
            label: 'sidebar.inspection',
            icon: 'fa fa-eye fa-list fa-fw',
            items: [
              {
                label: 'sidebar.inspectionList',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/master/inspection-list-list'],
              },
              {
                label: 'sidebar.method',
                icon: 'fa fa-eye fa-fw',
                routerLink: ['/master/inspection-method-list'],
              },
            ],
          },
          {
            label: 'Scenario',
            icon: 'fas fa-code-branch fa-fw',
            items: [
              {
                label: 'sidebar.measures',
                icon: 'fa fa-wrench fa-fw',
                routerLink: ['/master/measures'],
              },
              {
                label: 'sidebar.pricebook',
                icon: 'fa fa-wallet fa-fw',
                routerLink: ['/master/pricebook'],
              },
            ],
          },
        ],
      },
    ];
    menus.push(...companyMenus);

    if (this.permissionsService.hasModulePermission('masterdata', RoleAccess.READ, user)) {
      menus.push(...masterdataMenus);
    }

    const kibMenus = [
      {
        label: 'sidebar.kib',
        icon: 'fa fa-database fa-fw',
        items: [
          {
            label: 'sidebar.kibItems.inspectionList',
            icon: 'fa fa-eye fa-fw',
            routerLink: ['/kib/inspection-list-list'],
          },
          // {
          //   label: 'sidebar.method',
          //   icon: 'fa fa-eye fa-fw',
          //   routerLink: ['/kib/inspection-method-list'],
          // },
        ],
      },
    ];

    if (
      this.permissionsService.userRoles.includes('kib_admin') ||
      this.permissionsService.userRoles.includes('kib_user')
    ) {
      if (menus.length > 0) {
        menus.push({ separator: true });
      }

      if (this.permissionsService.userRoles.includes('kib_admin')) {
        kibMenus[0].items.push(
          {
            label: 'sidebar.apiAccount',
            icon: 'fa fa-key fa-fw',
            routerLink: ['/kib/api-account-list'],
          },
          {
            label: 'sidebar.users',
            icon: 'fa fa-users fa-fw',
            routerLink: ['/kib/user-list'],
          },
        );
      }

      menus.push(...kibMenus);
    }

    return menus;
  }
}
